import { isEmpty, isEqual } from 'lodash';
import React, { useEffect, useState } from 'react';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { FormikProps } from 'formik';
import { useSelector } from 'react-redux';
import { useFormCleanup } from '../../../TCSMApp/global/hooks/useFormCleanup';
import { EcdStates } from '../../root/ecd-model';
import { BaseApiState } from '../../../TCSMApp/global/models/api-model';
import { FormButtons } from './form-element/form-buttons';
import { FormikErrorSummary } from './form-element/formik-error-summary';
import { GlobalErrorMessage } from './global-error-message';
import { cleargyLocationAutocompletePath, cleargyNameAutocompletePath } from '../ducks/clergy-api';
import { parishLocationAutocompletePath, parishNameAutocompletePath } from '../ducks/parish-api';

interface FormWrapperProps {
    formikProps: FormikProps<any>;
    submitText: string;
    cancelText: string;
    onSubmit?: () => any;
    onCancel: () => any;
    submitDisabled?: boolean;
    formFooter?: JSX.Element;
    hideButtons?: boolean;
    hideCancelButton?: boolean;
    helperText?: string;
    generalErrorMessage?: string;
    formKeys?: string[];
    preventButtonDisableValidation?: boolean;
    initialValue?: any;
}

export function FormWrapper(props: React.PropsWithChildren<FormWrapperProps>) {
    const apiState = useSelector<EcdStates, BaseApiState>(state => state.apiState);    
    
    function usePendingRequests(baseApiState: BaseApiState): boolean {        
    
        return baseApiState?.pendingRequests?.filter(request =>            
            !request.includes(cleargyNameAutocompletePath) && 
            !request.includes(cleargyLocationAutocompletePath) &&
            !request.includes(parishNameAutocompletePath) && 
            !request.includes(parishLocationAutocompletePath) &&
            !request.includes('api/v1/errorlogging')).length > 0;
    }

    const pendingRequests = usePendingRequests(apiState);    
    
    const values = props.formikProps?.values;
    const initialValues = props.initialValue || props.formikProps?.initialValues;
    const [hasNoChanges, setHasNoChanges] = useState(false);

    useEffect(() => {
        const calcNoChanges = values && initialValues ? isEqual(initialValues, values) : false;
        setHasNoChanges(props.preventButtonDisableValidation ? false : calcNoChanges);		
	}, [initialValues, values, props.preventButtonDisableValidation]);

    useFormCleanup();        

    if (pendingRequests) {
        return <LoadingCircle />;
    }

    const hasFormikErrors = !isEmpty(props.formikProps.errors);

    const onCancel = () => {        
        props.onCancel();
    };

    const onSubmit = () => {
        window.scroll({ top: 0, left: 0, behavior: 'smooth' });

        if (props.onSubmit) {
            props.onSubmit();
        }
    };

    return (
        <>
            <GlobalErrorMessage generalErrorMessage={hasFormikErrors ? '' : props.generalErrorMessage} />
            <FormikErrorSummary formikProps={props.formikProps} formKeys={props.formKeys} onCancel={props.onCancel} />            
            {
                props.children
            }
            {props.formFooter}            
            {
                !props.hideButtons &&
                <FormButtons
                    disabled={apiState.pendingRequests?.length > 0}
                    submitText={props.submitText}
                    cancelText={props.cancelText}
                    onSubmit={onSubmit}
                    onCancel={onCancel}
                    submitDisabled={props.submitDisabled || hasNoChanges}
                    helperText={props.helperText}
                    hideCancelButton={props.hideCancelButton}
                />
            }
        </>
    );
}

FormWrapper.defaultProps = {
    onSubmit: null,
    submitDisabled: false,
    formFooter: null,
    hideButtons: false,
    hideCancelButton: false,
    helperText: '',
    generalErrorMessage: '',
    formKeys: [],
    preventButtonDisableValidation: false,
    initialValue: null,
};